import type React from 'react'
import {Suspense, useEffect} from 'react'
import {useDispatch} from 'react-redux'
import {resetTrendsFilters} from '@/store/slices/filtersSlice'
import BaseCard from '@/components/common/BaseCard/BaseCard'
import BreadcrumbWrapper from '@/components/GDS/molecules/Breadcrumb/BreadcrumbWrapper'
import {TabsNavigationWrapper} from '@/components/GDS/molecules/TabsNavigation'
import {TabWrapper} from '@/components/GDS/molecules/Tab'
import LoadingSpinner from '@/components/GDS/atoms/LoadingSpinner'
import {lazyLoad} from '@/utils/helperMethods'
import {useUrlTab} from '@/hooks/useUrlTab'

const SEMetricsTab = lazyLoad(() => import('./tabs/SEMetricsTab/SEMetricsTab'))
const VulnerabilitiesTab = lazyLoad(() => import('./tabs/VulnerabilitiesTab/VulnerabilitiesTab'))

const PAGE_TITLE = 'Trends'
const BREADCRUMB_ITEMS = [
  {id: 'home', label: 'Home', href: '/'},
  {id: 'se-metrics-trends', label: 'Trends', href: '/se-trends'},
]

const SEMetricsTrends: React.FC = () => {
  const tabConfigs = [
    {id: 'se-metrics', label: 'SE Metrics'},
    {id: 'vulnerabilities', label: 'Vulnerabilities'},
  ]

  const {activeTab: activeTabId, setActiveTab: setActiveTabId} = useUrlTab(tabConfigs[0].id)
  const activeTopTabIndex = tabConfigs.findIndex(tab => tab.id === activeTabId)

  const dispatch = useDispatch()

  // Clear the shared Trends filters when leaving the page so a fresh entry re-defaults to the first program.
  useEffect(
    () => () => {
      dispatch(resetTrendsFilters())
    },
    [dispatch],
  )

  const handleTopTabChange = (newIndex: number): void => {
    const newTabId = tabConfigs[newIndex]?.id
    if (newTabId) setActiveTabId(newTabId)
  }

  const handleTopTabClick = (indexClicked?: number): void => {
    if (indexClicked !== undefined) {
      const newTabId = tabConfigs[indexClicked]?.id
      if (newTabId) setActiveTabId(newTabId)
    }
  }

  return (
    <div className="mt-4 w-full">
      <div className="mb-4">
        <BreadcrumbWrapper breadcrumbs={BREADCRUMB_ITEMS} />
      </div>
      <BaseCard title={PAGE_TITLE}>
        <div className="divider-after-line relative mb-2 w-full overflow-visible">
          <div className="relative">
            <TabsNavigationWrapper
              data-testid="se-metrics-trends-tabs-navigation"
              className="select-none"
              activeTabIndex={activeTopTabIndex}
              handleSetActiveTab={handleTopTabChange}
              type="line"
              orientation="horizontal"
            >
              {tabConfigs.map((tab, index) => (
                <TabWrapper
                  data-testid={`se-metrics-trends-tab-${tab.id}`}
                  key={tab.id}
                  label={tab.label}
                  index={index}
                  isActive={activeTopTabIndex === index}
                  onClick={(): void => handleTopTabClick(index)}
                  className="select-none"
                />
              ))}
            </TabsNavigationWrapper>
          </div>
        </div>

        <Suspense
          fallback={
            <div className="flex h-[400px] items-center justify-center">
              <LoadingSpinner />
            </div>
          }
        >
          {activeTopTabIndex === 0 && <SEMetricsTab />}
          {activeTopTabIndex === 1 && <VulnerabilitiesTab />}
        </Suspense>
      </BaseCard>
    </div>
  )
}

export default SEMetricsTrends
